/**
 * Admin — Usage analytics tab (events from /api/analytics/summary).
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    ChartLineUp, Users, PlayCircle, ListChecks, Trophy, Path, Star,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { timingCurrentFpl } from "../../lib/timing-pool-api";
import { Card, TopList } from "./adminUi";

const RANGES = [7, 14, 30, 60, 90];
const TOOL_LABELS = {
    timing: "Timing / DNA",
    inko: "INKO",
    lineup: "Lineup Sim",
    market: "Trhy",
    predictions: "Predikce",
    hidden: "Skryté klenoty",
};

function DailyChart({ daily }) {
    if (!daily?.length) {
        return (
            <div className="border border-white/10 bg-[#0d0d0d] p-4 text-center font-mono text-[10px] text-white/40">
                Žádná data v tomto rozsahu.
            </div>
        );
    }
    const maxEvents = Math.max(...daily.map((d) => d.events), 1);
    const maxSessions = Math.max(...daily.map((d) => d.sessions), 1);
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-4 space-y-3" data-testid="analytics-daily">
            <div className="flex items-center justify-between gap-2">
                <h3 className="font-display font-black uppercase tracking-tight text-sm flex items-center gap-2">
                    <ChartLineUp size={12} weight="fill" className="text-fa-sky" />
                    Denní aktivita
                </h3>
                <div className="flex items-center gap-3 font-mono text-[9px] uppercase tracking-widest text-white/40">
                    <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 bg-fa-yellow" /> Události</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 bg-fa-sky" /> Návštěvy</span>
                </div>
            </div>
            <div className="flex items-end gap-0.5 h-32">
                {daily.map((d) => (
                    <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                        <div className="w-full flex items-end justify-center gap-0.5 flex-1">
                            <div
                                className="w-1/2 bg-fa-yellow/80 group-hover:bg-fa-yellow"
                                style={{ height: `${Math.max(2, (d.events / maxEvents) * 100)}%` }}
                            />
                            <div
                                className="w-1/2 bg-fa-sky/70 group-hover:bg-fa-sky"
                                style={{ height: `${Math.max(2, (d.sessions / maxSessions) * 100)}%` }}
                            />
                        </div>
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black border border-white/20 px-2 py-1 text-[10px] font-mono whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10">
                            <div className="text-white/90">{d.date}</div>
                            <div className="text-fa-yellow">Ev: <span className="tabular-nums">{d.events}</span></div>
                            <div className="text-fa-sky">Nvs: <span className="tabular-nums">{d.sessions}</span></div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex justify-between font-mono text-[9px] text-white/40 tabular-nums">
                <span>{daily[0].date}</span>
                <span>{daily[daily.length - 1].date}</span>
            </div>
        </div>
    );
}

export default function AdminUsage() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [playerLookup, setPlayerLookup] = useState({});

    const load = async (d = days) => {
        setBusy(true);
        try {
            const r = await api.get(`/analytics/summary`, { params: { days: d } }).then((r) => r.data);
            setData(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Nepodařilo se načíst analytiku");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(days); }, [days]);

    useEffect(() => {
        (async () => {
            try {
                const pool = await timingCurrentFpl();
                const map = {};
                for (const p of pool.players || []) {
                    if (p.fpl_id) map[String(p.fpl_id)] = p;
                }
                setPlayerLookup(map);
            } catch { /* ignore */ }
        })();
    }, []);

    const totals = data?.totals || {};
    const eventsByType = data?.events_by_type || [];

    const toolsRows = useMemo(
        () => (data?.tools || []).map((t) => ({
            id: t.tool,
            label: TOOL_LABELS[t.tool] || t.tool,
            count: t.count,
            pct: null,
        })),
        [data],
    );

    return (
        <div className="space-y-6" data-testid="admin-usage">
            <div className="flex items-center gap-2 flex-wrap font-mono text-[11px]">
                <span className="text-white/50">Období:</span>
                {RANGES.map((r) => (
                    <button
                        key={r}
                        type="button"
                        onClick={() => setDays(r)}
                        disabled={busy}
                        data-testid={`analytics-range-${r}`}
                        className={`px-2 py-1 border uppercase tracking-widest text-[10px] ${days === r ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                    >
                        {r} d
                    </button>
                ))}
                <button
                    type="button"
                    onClick={() => load(days)}
                    disabled={busy}
                    data-testid="analytics-refresh-btn"
                    className="ml-auto border border-white/20 px-3 py-1.5 hover:border-white/40 text-white/70 hover:text-white uppercase tracking-widest text-[10px] disabled:opacity-40"
                >
                    {busy ? "Načítám…" : "Aktualizovat"}
                </button>
            </div>

            <section className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="analytics-kpis">
                <Card label="Celkem událostí" value={totals.events?.toLocaleString("cs") ?? "—"} icon={ListChecks} accent="text-white" testid="kpi-events" />
                <Card label="Návštěvy (unique)" value={totals.unique_sessions?.toLocaleString("cs") ?? "—"} icon={Users} accent="text-fa-sky" testid="kpi-sessions" sub={`${totals.unique_users ?? 0} přihlášených`} />
                <Card label="Simulací sezóny" value={totals.sim_runs?.toLocaleString("cs") ?? "—"} icon={PlayCircle} accent="text-fa-yellow" testid="kpi-sims" />
                <Card label="Pool analýz" value={totals.pool_runs?.toLocaleString("cs") ?? "—"} icon={Trophy} accent="text-fa-mint" testid="kpi-pool" />
                <Card label="Období" value={`${days} d`} icon={Path} accent="text-white/80" sub={data?.since ? new Date(data.since).toLocaleDateString("cs") : "—"} testid="kpi-range" />
            </section>

            <section>
                <DailyChart daily={data?.daily} />
            </section>

            <section className="grid md:grid-cols-2 gap-3">
                <TopList title="Nejpoužívanější nástroje" rows={toolsRows} icon={ListChecks} accent="text-fa-sky" testid="analytics-tools" />
                <TopList title="Nejnavštěvovanější stránky" rows={(data?.pages || []).map((p) => ({ id: p.path, label: p.path, count: p.views }))} icon={Path} accent="text-fa-mint" testid="analytics-pages" />
            </section>

            <section className="grid md:grid-cols-2 gap-3">
                <TopList title="TOP hráči v simulacích" rows={data?.top_sim_players || []} icon={Star} accent="text-fa-yellow" testid="analytics-sim-players" playerLookup={playerLookup} />
                <TopList title="TOP kapitáni v simulacích" rows={data?.top_captains || []} icon={Trophy} accent="text-fa-yellow" testid="analytics-captains" playerLookup={playerLookup} />
            </section>

            <section>
                <TopList title="TOP hráči v pool analýzách" rows={data?.top_pool_players || []} icon={Users} accent="text-fa-mint" testid="analytics-pool-players" playerLookup={playerLookup} />
            </section>

            {eventsByType.length > 0 && (
                <section className="border border-white/10 bg-[#0d0d0d] p-4 space-y-2" data-testid="analytics-events-by-type">
                    <h3 className="font-display font-black uppercase tracking-tight text-sm">Události podle typu</h3>
                    <ul className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {eventsByType.map((e) => (
                            <li key={e.event} className="flex items-center justify-between border border-white/10 bg-black/40 px-2 py-1.5">
                                <span className="font-mono text-[10px] uppercase tracking-widest text-white/60">{e.event}</span>
                                <span className="font-mono text-[11px] tabular-nums text-white">{e.count}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
}
